<script>
import HttpClient from '../helpers/HttpClient.js';
import { sendGtmEvent } from '../helpers/gtmHandler.js';
import SeriesSelectorText from './SeriesSelectorText.vue';
import SeriesSelectorStepNav from './SeriesSelectorStepNav.vue';
import SeriesSelectorHelp from './SeriesSelectorHelp.vue';
import SeriesSelectorAnswers from './SeriesSelectorAnswers.vue';
import SeriesSelectorResults from './SeriesSelectorResults.vue';
import SeriesSelectorHubspotForm from './SeriesSelectorHubspotForm.vue';
import SeriesSelectorThankYou from './SeriesSelectorThankYou.vue';

export default {
    name: 'SeriesSelector',
    components: {
        SeriesSelectorThankYou,
        SeriesSelectorText,
        SeriesSelectorStepNav,
        SeriesSelectorAnswers,
        SeriesSelectorHelp,
        SeriesSelectorResults,
        SeriesSelectorHubspotForm
    },
    props: {
        locale: { type: String, default: null },
        contactButtonText: { type: String, default: null },
        contactDescriptionText: { type: String, default: null },
        contactFormTexts: { type: Object, required: true },
        previousButtonText: { type: String, default: null },
        resultsTexts: { type: Object, required: true },
        segments: { type: Array, required: true },
        steps: { type: Array, required: true },
        thankYouTexts: { type: Object, required: true },
        title: { type: String, default: null },
        hubspotFormData: { type: Object, default: null },
    },
    data() {
        return {
            httpClient: new HttpClient( `/${this.locale}/api/feature/seriesselector`),
            previousStep: 0,
            step: 0,
            questionId: this.steps[0].questionId,
            question: this.steps[0].question || '',
            answerOptions: [],
            answers: [],
            selectedSegmentId: null,
            selectedApplicationId: null,
            modelSeriesResults: [],
            isLoading: false,
            isVisible: false,
            observer: null
        };
    },
    watch: {
        step(newValue, oldValue) {
            this.previousStep = oldValue;
            if (newValue < this.steps.length) {
                this.questionId = this.steps[newValue].questionId;
                this.question = this.steps[newValue].question;
            }
            if (newValue === 0) {
                this.answerOptions = this.segments;
                this.selectedSegmentId = null;
                this.selectedApplicationId = null;
                this.answers = [];
                this.modelSeriesResults = [];
            } else if (newValue === 1) {
                this.getAnswerOptions();
            } else if (newValue === this.steps.length) {
                this.getModelSeriesResults();
            } else if (newValue === this.steps.length + 2) {
                sendGtmEvent({
                    step: 8,
                    step_name: 'Thank you',
                    result: this.modelSeriesResults.map(m => m.Name).join(', '),
                    truck_segment: this.answers[0] ? this.answers[0].answer : '',
                    truck_application: this.answers[1] ? this.answers[1].answer : '',
                    truck_chassis: this.answers[2] ? this.answers[2].answer : '',
                    truck_axle: this.answers[3] ? this.answers[3].answer : '',
                }, this.$refs.seriesSelector);
            }
            this.answers = this.answers.filter((answer) => answer.step < newValue);
        },
        isVisible(newValue, oldValue) {
            if (newValue !== oldValue) {
                if (newValue) {
                    sendGtmEvent({
                        eventName: 'series_selector',
                        step: 0,
                        step_name: 'start series selector',
                    }, this.$refs.seriesSelector);
                }
            }
        }
    },
    mounted() {
        this.answerOptions = this.segments;
        this.setupIntersectionObserver();
    },
    beforeUnmount() {
        this.cleanupIntersectionObserver();
    },
    methods: {
        setupIntersectionObserver() {
            if (!this.$refs.seriesSelector || this.observer) return;

            const options = {
                threshold: [0, 0.1, 0.5, 1.0],
                rootMargin: '0px'
            };

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (this.isVisible) return;
                    this.isVisible = entry.isIntersecting;
                });
            }, options);

            this.observer.observe(this.$refs.seriesSelector);
        },
        cleanupIntersectionObserver() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        },
        async getAnswerOptions() {
            try {
                this.isLoading = true;
                this.answerOptions = await this.httpClient.post('/gettransportapplications', {
                    businessSegmentId: this.selectedSegmentId,
                });
            } catch (error) {
                console.error('Error fetching transport applications:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async getModelSeriesResults() {
            try {
                this.isLoading = true;
                this.modelSeriesResults = await this.httpClient.post('/getmodelseries', {
                    businessSegmentId: this.selectedSegmentId,
                    transportApplicationId: this.selectedApplicationId
                });
                sendGtmEvent({
                    step: 6,
                    step_name: 'Results',
                    result: this.modelSeriesResults.map(m => m.Name).join(', '),
                    truck_segment: this.answers[0] ? this.answers[0].answer : '',
                    truck_application: this.answers[1] ? this.answers[1].answer : '',
                    truck_chassis: this.answers[2] ? this.answers[2].answer : '',
                    truck_axle: this.answers[3] ? this.answers[3].answer : '',
                }, this.$refs.seriesSelector);
            } catch (error) {
                console.error('Error fetching model series results:', error);
            } finally {
                this.isLoading = false;
            }
        },
        answer(answer) {
            if (this.step === 0) {
                this.selectedSegmentId = answer.id;
            }
            if (this.step === 1) {
                this.selectedApplicationId = answer.id;
            }
            this.answers.push({
                questionId: this.steps[this.step].questionId,
                question: this.steps[this.step].question,
                stepName: this.steps[this.step].name,
                step: this.step,
                answerId: answer.id,
                answer: answer.name,
            });
            sendGtmEvent({
                step: this.step + 1,
                step_name: this.step === 0 ? 'Segment' : 'Application',
                selected_option: answer.name
            }, this.$refs.seriesSelector);
            this.step++;
        },
        contactDealer() {
            sendGtmEvent({
                event: 'series_selector_contact_dealer',
                step: this.step + 1,
                step_name: this.step === 0 ? 'Segment' : 'Application',
                link_text: this.contactButtonText,
                link_url: '',
                link_classes: 'btn btn-ghost'
            }, this.$refs.seriesSelector);
            this.step = this.steps.length + 1;
        },
        sendToDealer() {
            sendGtmEvent({
                event: 'series_selector_send_to_dealer',
                link_text: this.resultsTexts.sendToDealerLinkText,
                link_url: '',
                link_classes: 'btn btn-primary',
                result: this.modelSeriesResults.map(m => m.Name).join(', '),
            }, this.$refs.seriesSelector);
            this.step = this.steps.length + 1;
        },
        trackConfigureTruckEvent(modelName) {
            sendGtmEvent({
                event: 'series_selector_configure_this_truck',
                result: modelName,
                step: this.step === this.steps.length + 2 ? 8 : 6,
                step_name: this.step === this.steps.length + 2 ? 'Thank you' : 'Results',
                truck_segment: this.answers[0] ? this.answers[0].answer : '',
                truck_application: this.answers[1] ? this.answers[1].answer : '',
                truck_chassis: this.answers[2] ? this.answers[2].answer : '',
                truck_axle: this.answers[3] ? this.answers[3].answer : '',
            }, this.$refs.seriesSelector);
        },
        handleFormSubmitted() {
            sendGtmEvent({
                event: 'series_selector_request_contact_dealer',
                result: this.modelSeriesResults.map(m => m.Name).join(', '),
                truck_segment: this.answers[0] ? this.answers[0].answer : '',
                truck_application: this.answers[1] ? this.answers[1].answer : '',
                truck_chassis: this.answers[2] ? this.answers[2].answer : '',
                truck_axle: this.answers[3] ? this.answers[3].answer : '',
            }, this.$refs.seriesSelector);
            this.step = this.steps.length + 2;
        }
    }
};
</script>

<template>
    <div ref="seriesSelector" class="series-selector" data-dm="component.seriesselector">
        <div v-show="step !== steps.length + 2">
            <div v-show="step < steps.length">
                <SeriesSelectorText
                    :title="title"
                    :question="question"
                />
                <SeriesSelectorStepNav
                    :steps="steps"
                    :question-id="questionId"
                    @select="(index) => (step = index)"
                >
                    <SeriesSelectorAnswers
                        class="mobile-only"
                        :answers="answerOptions"
                        :steps="steps"
                        :question-id="questionId"
                        @answer="answer($event)"
                    />
                </SeriesSelectorStepNav>
                <SeriesSelectorAnswers
                    class="desktop-only"
                    :answers="answerOptions"
                    :steps="steps"
                    :question-id="questionId"
                    @answer="answer($event)"
                />
                <SeriesSelectorHelp
                    :description="contactDescriptionText"
                    :text="contactButtonText"
                    @on-contact-dealer="contactDealer"
                />
            </div>
            <SeriesSelectorResults
                v-show="step === steps.length"
                :previous-button-text="resultsTexts.previousButtonText"
                :send-to-dealer-link-text="resultsTexts.sendToDealerLinkText"
                :text="resultsTexts.text"
                :title="resultsTexts.title"
                :results="modelSeriesResults"
                :is-loading="isLoading"
                @configure-truck="trackConfigureTruckEvent"
                @select-previous-step="step = steps.length - 1"
                @send-to-dealer="sendToDealer"
            />
            <SeriesSelectorHubspotForm
                v-show="step === steps.length + 1"
                :range="hubspotFormData.range"
                :locale="hubspotFormData.locale"
                :form-id="hubspotFormData.formId"
                :portal-id="hubspotFormData.portalId"
                :initial-country="hubspotFormData.initialCountry"
                :countries="hubspotFormData.countries"
                :title="contactFormTexts.title"
                :answers-text="contactFormTexts.answersText"
                :results-text="contactFormTexts.resultsText"
                :answers="answers"
                :previous-button-text="previousButtonText"
                :field-names="{
                    dealer: hubspotFormData.dealerFieldName,
                    country: hubspotFormData.countryFieldName,
                    zipCode: hubspotFormData.cityFieldName
                }"
                :dealer-types="hubspotFormData.dealerTypes"
                :results="modelSeriesResults"
                @select-previous-step="step = previousStep"
                @form-submitted="handleFormSubmitted"
            />
        </div>
        <div v-show="step === steps.length + 2">
            <SeriesSelectorThankYou
                :back-to-start-button-text="thankYouTexts.backToStartButtonText"
                :results="modelSeriesResults"
                :title="thankYouTexts.title"
                :is-loading="isLoading"
                @go-back-to-start="step = 0"
                @configure-truck="trackConfigureTruckEvent"
            />
        </div>
    </div>
</template>
